import React from 'react';

export const RankingTable = ({ data, league, groupName, showLast5Matches }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-400">
        <p className="text-lg font-medium">Không có dữ liệu</p>
      </div>
    );
  }

  const sortedData = [...data].sort((a, b) => a.rank - b.rank);

  const renderMatchResult = (result) => {
    const num = typeof result === 'string' ? parseInt(result, 10) : result;

    const map = {
      0: { label: 'Thắng', color: 'bg-green-500', text: 'W' },
      1: { label: 'Hòa', color: 'bg-gray-400', text: 'D' },
      2: { label: 'Thua', color: 'bg-red-500', text: 'L' },
    };

    const cfg = map[num];
    if (!cfg) return null;

    return (
      <span
        title={cfg.label}
        className={`inline-flex items-center justify-center
          w-5 h-5 rounded-full text-white text-xs font-bold ${cfg.color}`}
      >
        {cfg.text}
      </span>
    );
  };

  const getRankBorderColor = (index, total) => {
    if (groupName) {
      return index < 2 ? 'border-l-4 border-green-500' : '';
    }

    if (index >= total - 3) return 'border-l-4 border-red-500';
    if (index < 4) return 'border-l-4 border-green-500';
    if (index < 6) return 'border-l-4 border-yellow-500';
    return '';
  };

  return (
    <div className="w-full">
      {league && (
        <div className="flex items-center gap-3 mb-4 p-4 rounded-lg
          bg-[var(--theme-bg)]">
          {league.logo && (
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
              <img
                src={league.logo}
                alt={league.name}
                className="w-8 h-8 object-contain"
              />
            </div>
          )}
          <div>
            <h3 className="text-lg font-bold">
              {league.name}
              {groupName && <span className="ml-2">- {groupName}</span>}
            </h3>
            {league.currentRound && (
              <p className="text-sm">
                Vòng {league.currentRound}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="overflow-x-auto border rounded-lg bg-white">
        <table className="min-w-[900px] w-full border-collapse">
          <thead className="bg-[var(--theme-accent)] text-xs font-semibold text-[var(--theme-text)]">
          <tr>
            <th className="px-3 py-2 text-center w-12">#</th>
            <th className={`px-3 py-2 text-left ${showLast5Matches ? 'min-w-[120px]' : 'w-12'}`}>Đội bóng</th>
            <th className="px-3 py-2 text-center w-16">Trận</th>
            <th className="px-3 py-2 text-center w-12">T</th>
            <th className="px-3 py-2 text-center w-12">H</th>
            <th className="px-3 py-2 text-center w-12">B</th>
            <th className="px-3 py-2 text-center w-14">BT</th>
            <th className="px-3 py-2 text-center w-14">BB</th>
            <th className="px-3 py-2 text-center w-14">HS</th>
            <th className="px-3 py-2 text-center w-14">Điểm</th>
            {showLast5Matches && (
              <th className="px-3 py-2 text-center min-w-[112px]">5 trận</th>
            )}
          </tr>
          </thead>

          <tbody className="text-sm text-[var(--theme-text)]">
          {sortedData.map((team, index) => {
            const points =
              team.integral ??
              team.winCount * 3 + team.drawCount;

            const recentResults = [
              team.recentSixthResult,
              team.recentFifthResult,
              team.recentFourthResult,
              team.recentThirdResult,
              team.recentSecondResult,
              team.recentFirstResult,
            ].filter(Boolean);

            return (
              <tr
                key={team.id || index}
                className={`
                    ${index % 2 === 0 ? 'bg-[var(--theme-foreground)]' : 'bg-[var(--theme-bg)]'}
                    hover:bg-[var(--theme-accent)]
                    ${getRankBorderColor(index, sortedData.length)}
                  `}
              >
                <td className="px-3 py-2 text-center font-bold">
                  {team.rank}
                </td>

                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {team.teams?.logo ? (
                      <img
                        src={team.teams.logo}
                        alt={team.teams.name}
                        className="w-6 h-6 object-contain"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-[var(--theme-bg)]
                          flex items-center justify-center text-xs">
                        ?
                      </div>
                    )}
                    <span className="truncate font-medium">
                        {team.teams?.name || `Team #${team.teamId}`}
                      </span>
                  </div>
                </td>

                <td className="px-3 py-2 text-center">{team.totalCount}</td>
                <td className="px-3 py-2 text-center">{team.winCount}</td>
                <td className="px-3 py-2 text-center">{team.drawCount}</td>
                <td className="px-3 py-2 text-center">{team.loseCount}</td>
                <td className="px-3 py-2 text-center">{team.getScore}</td>
                <td className="px-3 py-2 text-center">{team.loseScore}</td>
                <td className="px-3 py-2 text-center">
                  {team.goalDifference > 0
                    ? `+${team.goalDifference}`
                    : team.goalDifference}
                </td>

                <td className="px-3 py-2 text-center font-bold">
                  {points}
                </td>

                {showLast5Matches && (
                  <td className="px-3 py-2">
                  <div className="flex justify-center gap-1">
                    {recentResults.map((r, i) => (
                      <React.Fragment key={i}>
                        {renderMatchResult(r)}
                      </React.Fragment>
                    ))}
                  </div>
                </td>
                )}
              </tr>
            );
          })}
          </tbody>
        </table>
      </div>

      {!groupName && (
        <div className="mt-6 flex flex-wrap gap-4 text-xs py-2">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 border-l-4 border-green-500" />
            Top 4 – Champions League
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 border-l-4 border-yellow-500" />
            Top 5–6 – Europa League
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 border-l-4 border-red-500" />
            Xuống hạng
          </div>
        </div>
      )}
    </div>
  );
};
